import { useMemo, useRef, useState } from 'react'

import { CalendarAppointmentBlock } from '@/components/calendar/CalendarAppointmentBlock'
import { layoutAppointmentLanes } from '@/lib/calendar/appointmentLayout'
import {
  PX_PER_MINUTE,
  buildReschedulePayload,
  buildTimeSlotLabels,
  dropOffsetToMinutes,
  parseTimeToMinutes,
} from '@/lib/calendar/dayCalendar'
import { cn } from '@/lib/utils'

const UNASSIGNED_KEY = 'unassigned'

/**
 * @param {import('@/types/api').Appointment[]} appointments
 * @param {import('@/types/api').Stylist[]} stylists
 */
function buildColumns(stylists, appointments) {
  const columns = stylists
    .filter((stylist) => stylist.is_active)
    .map((stylist) => ({
      key: String(stylist.id),
      stylistId: stylist.id,
      label: stylist.name,
      colour: stylist.colour,
    }))

  const hasUnassigned = appointments.some(
    (appointment) =>
      !appointment.stylist_id ||
      !stylists.some((stylist) => stylist.id === appointment.stylist_id),
  )

  if (hasUnassigned) {
    columns.push({
      key: UNASSIGNED_KEY,
      stylistId: null,
      label: 'Unassigned',
      colour: '#94a3b8',
    })
  }

  return columns
}

/**
 * @param {import('@/types/api').Appointment} appointment
 */
function columnKeyForAppointment(appointment, stylistIds) {
  if (!appointment.stylist_id || !stylistIds.has(appointment.stylist_id)) {
    return UNASSIGNED_KEY
  }

  return String(appointment.stylist_id)
}

/**
 * @param {{
 *   date: string
 *   appointments: import('@/types/api').Appointment[]
 *   stylists: import('@/types/api').Stylist[]
 *   openTime: string
 *   closeTime: string
 *   slotMinutes: number
 *   onReschedule: (appointmentId: number, payload: { starts_at: string, stylist_id?: number | null }) => Promise<void>
 *   onSelectAppointment?: (appointment: import('@/types/api').Appointment) => void
 *   rescheduleEnabled?: boolean
 *   reschedulingId?: number | null
 * }} props
 */
export function DayCalendar({
  date,
  appointments,
  stylists,
  openTime,
  closeTime,
  slotMinutes,
  onReschedule,
  onSelectAppointment,
  rescheduleEnabled = false,
  reschedulingId = null,
}) {
  const [draggingId, setDraggingId] = useState(null)
  const columnRefs = useRef({})

  const dayStartMinutes = parseTimeToMinutes(openTime)
  const dayEndMinutes = parseTimeToMinutes(closeTime)
  const gridHeight = (dayEndMinutes - dayStartMinutes) * PX_PER_MINUTE
  const slots = buildTimeSlotLabels(dayStartMinutes, dayEndMinutes, slotMinutes)

  const columns = useMemo(() => buildColumns(stylists, appointments), [stylists, appointments])
  const stylistIds = useMemo(() => new Set(stylists.map((stylist) => stylist.id)), [stylists])

  const appointmentsByColumn = useMemo(() => {
    const grouped = Object.fromEntries(columns.map((column) => [column.key, []]))

    for (const appointment of appointments) {
      const key = columnKeyForAppointment(appointment, stylistIds)
      if (!grouped[key]) {
        grouped[key] = []
      }
      grouped[key].push(appointment)
    }

    return grouped
  }, [appointments, columns, stylistIds])

  const laneLayouts = useMemo(() => {
    /** @type {Record<string, Map<number, { column: number, totalColumns: number }>>} */
    const layouts = {}

    for (const column of columns) {
      layouts[column.key] = layoutAppointmentLanes(appointmentsByColumn[column.key] ?? [])
    }

    return layouts
  }, [appointmentsByColumn, columns])

  /**
   * @param {import('@/types/api').Appointment} appointment
   * @param {number | null} targetStylistId
   * @param {DragEvent} event
   * @param {string} columnKey
   */
  async function handleDrop(appointment, targetStylistId, event, columnKey) {
    event.preventDefault()

    const columnElement = columnRefs.current[columnKey]
    if (!columnElement) {
      return
    }

    const offsetY = event.clientY - columnElement.getBoundingClientRect().top + columnElement.scrollTop
    const dropMinutes = dropOffsetToMinutes(offsetY, dayStartMinutes, PX_PER_MINUTE, slotMinutes)
    const maxStart = dayEndMinutes - (appointment.duration_minutes ?? slotMinutes)

    if (dropMinutes < dayStartMinutes || dropMinutes > maxStart) {
      setDraggingId(null)
      return
    }

    const payload = buildReschedulePayload(appointment, targetStylistId, date, dropMinutes)

    try {
      await onReschedule(appointment.id, payload)
    } finally {
      setDraggingId(null)
    }
  }

  if (columns.length === 0) {
    return (
      <div className="card-surface p-8 text-center text-sm text-muted-foreground">
        Add active stylists in Masters to display the day calendar.
      </div>
    )
  }

  return (
    <div className="overflow-hidden">
      <div className="overflow-x-auto">
        <div
          className="grid min-w-max"
          style={{ gridTemplateColumns: `72px repeat(${columns.length}, minmax(180px, 1fr))` }}
        >
          <div className="sticky left-0 z-20 border-b border-r bg-muted/30 px-2 py-3 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            Time
          </div>

          {columns.map((column) => (
            <div
              key={column.key}
              className="border-b px-3 py-3"
              style={{ borderTop: `3px solid ${column.colour ?? 'hsl(var(--primary))'}` }}
            >
              <p className="truncate text-sm font-semibold text-foreground">{column.label}</p>
              <p className="text-[10px] text-muted-foreground">
                {(appointmentsByColumn[column.key] ?? []).length} booking
                {(appointmentsByColumn[column.key] ?? []).length === 1 ? '' : 's'}
              </p>
            </div>
          ))}

          <div className="sticky left-0 z-20 border-r bg-muted/20">
            <div className="relative" style={{ height: gridHeight }}>
              {slots.map((slot) => (
                <div
                  key={slot.minutes}
                  className="absolute left-0 right-0 border-t border-border/50 px-2 text-[10px] tabular-nums text-muted-foreground"
                  style={{ top: (slot.minutes - dayStartMinutes) * PX_PER_MINUTE }}
                >
                  <span className="relative -top-2 bg-muted/20 pr-1">{slot.label}</span>
                </div>
              ))}
            </div>
          </div>

          {columns.map((column) => (
            <div
              key={`${column.key}-body`}
              ref={(element) => {
                columnRefs.current[column.key] = element
              }}
              className={cn(
                'relative border-r border-border/60',
                draggingId && rescheduleEnabled && 'bg-primary/[0.02]',
              )}
              style={{ height: gridHeight }}
              onDragOver={(event) => {
                if (!rescheduleEnabled) {
                  return
                }

                event.preventDefault()
                event.dataTransfer.dropEffect = 'move'
              }}
              onDrop={(event) => {
                if (!rescheduleEnabled) {
                  return
                }

                const appointment = appointments.find((item) => item.id === draggingId)
                if (!appointment) {
                  return
                }

                handleDrop(appointment, column.stylistId, event, column.key)
              }}
            >
              {slots.map((slot) => (
                <div
                  key={`${column.key}-${slot.minutes}`}
                  className="pointer-events-none absolute left-0 right-0 border-t border-border/30"
                  style={{ top: (slot.minutes - dayStartMinutes) * PX_PER_MINUTE }}
                />
              ))}

              {(appointmentsByColumn[column.key] ?? []).map((appointment) => (
                <CalendarAppointmentBlock
                  key={appointment.id}
                  appointment={appointment}
                  stylistColour={column.colour}
                  dayStartMinutes={dayStartMinutes}
                  pxPerMinute={PX_PER_MINUTE}
                  lane={laneLayouts[column.key]?.get(appointment.id)}
                  rescheduling={reschedulingId === appointment.id}
                  rescheduleEnabled={rescheduleEnabled}
                  onDragStart={(item) => setDraggingId(item.id)}
                  onSelect={onSelectAppointment}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
